import { error as errorToast } from '@react/react-spectrum/Toast/js/ToastContainer';
import { useEffect, useState } from 'react';

import useIMS from './useIMS';

/**
 * Custom hook to handle API calls
 * @template T
 * @param {Object} props
 * @param {boolean} [props.shouldFetch] - Should the API call be made
 * @param {boolean} [props.shouldShowError] - Should the error be shown in a toast
 * @param {(response: any) => void} [props.onSuccess] - Callback function to be called on success
 * @param {{controller: AbortController, run: () => any}} props.handler - The handler object
 * @param {boolean} [props.defaultLoadingState] - Default loading state
 */
export default ({ shouldFetch = true, shouldShowError = true, onSuccess, handler, defaultLoadingState = false }) => {
  /**@type {ReturnType<typeof useState<T>} */
  const [response, setResponse] = useState(null);
  /**@type {ReturnType<typeof useState<import('@exchange-frontends/api/src/utils/CallError').default>} */
  const [error, setError] = useState();
  const [loading, setLoading] = useState(defaultLoadingState);
  const ims = useIMS();

  useEffect(() => {
    if (!shouldFetch || !handler || !ims?.initialized) {
      return;
    }
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const response = await handler.run();
        if (mounted) {
          setResponse(response);
          onSuccess?.(response);
        }
      } catch (err) {
        if (err.code === 429) {
          // CCES asked us to redirect to login on 429
          ims.signIn();
        } else if (shouldShowError && err.userMessage && mounted && err.code !== 20) {
          // 20 is the error code for aborted requests
          errorToast(err.userMessage, { timeout: 0 });
          setError(err);
        }
        console.error(err);
      }
      if (mounted) {
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
      handler.controller.abort();
    };
  }, [shouldFetch, handler, shouldShowError, ims, onSuccess]);

  return { response, loading, error };
};
